import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API_BASE } from '../../config/api';
import './css/AdminFAQEdit.css';

const emptyFaq = {
  id: null,
  question: '',
  answer: '',
  sort_order: 0,
  status: 'published',
};

const AdminFAQEdit = () => {
  const defaultSection = useMemo(
    () => ({
      header_title: '',
      header_subtitle: '',
      footer_title: '',
      footer_subtitle: '',
      footer_button_text: '',
      footer_button_link: '/contact',
    }),
    []
  );

  const [loading, setLoading] = useState(true);
  const [savingSection, setSavingSection] = useState(false);
  const [section, setSection] = useState(defaultSection);

  const [faqs, setFaqs] = useState([]);
  const [form, setForm] = useState(emptyFaq);
  const [savingFaq, setSavingFaq] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  async function loadAll() {
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      const [sectionRes, faqsRes] = await Promise.all([
        axios.get(`${API_BASE}/api/admin/faq-section`),
        axios.get(`${API_BASE}/api/admin/faqs`),
      ]);

      setSection({ ...defaultSection, ...(sectionRes?.data?.section || {}) });
      setFaqs(Array.isArray(faqsRes?.data?.faqs) ? faqsRes.data.faqs : []);
    } catch (e) {
      console.error(e);
      setError('فشل تحميل بيانات الأسئلة الشائعة.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function startCreate() {
    setForm({ ...emptyFaq, sort_order: faqs.length ? Math.max(...faqs.map((f) => f.sort_order || 0)) + 1 : 1 });
    setSuccess('');
    setError('');
  }

  function startEdit(faq) {
    setForm({
      id: faq.id,
      question: faq.question || '',
      answer: faq.answer || '',
      sort_order: faq.sort_order ?? 0,
      status: faq.status || 'published',
    });
    setSuccess('');
    setError('');
  }

  function cancelEdit() {
    setForm(emptyFaq);
    setSuccess('');
    setError('');
  }

  async function saveSection(e) {
    e.preventDefault();
    setSavingSection(true);
    setError('');
    setSuccess('');
    try {
      await axios.put(`${API_BASE}/api/admin/faq-section`, section);
      setSuccess('تم حفظ إعدادات الصفحة.');
    } catch (e2) {
      console.error(e2);
      setError('فشل حفظ إعدادات الصفحة.');
    } finally {
      setSavingSection(false);
    }
  }

  async function saveFaq(e) {
    e.preventDefault();
    setSavingFaq(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        question: form.question,
        answer: form.answer,
        sort_order: form.sort_order,
        status: form.status,
      };
      if (form.id) {
        await axios.put(`${API_BASE}/api/admin/faqs/${form.id}`, payload);
        setSuccess('تم تحديث السؤال.');
      } else {
        await axios.post(`${API_BASE}/api/admin/faqs`, payload);
        setSuccess('تم إضافة السؤال.');
      }
      await loadAll();
      setForm(emptyFaq);
    } catch (e2) {
      console.error(e2);
      setError('فشل حفظ السؤال.');
    } finally {
      setSavingFaq(false);
    }
  }

  async function deleteFaq(id) {
    const ok = window.confirm('هل تريد حذف هذا السؤال؟');
    if (!ok) return;
    setDeletingId(id);
    setError('');
    setSuccess('');
    try {
      await axios.delete(`${API_BASE}/api/admin/faqs/${id}`);
      setSuccess('تم حذف السؤال.');
      await loadAll();
    } catch (e) {
      console.error(e);
      setError('فشل حذف السؤال.');
    } finally {
      setDeletingId(null);
    }
  }

  const isEditing = Boolean(form.id);

  return (
    <div className="admin-faq-edit">
      <div className="admin-faq-header">
        <h2>تعديل صفحة الأسئلة الشائعة</h2>
        <p>تعديل العناوين وإضافة/تحديث/حذف الأسئلة.</p>
      </div>

      {loading ? (
        <div className="admin-faq-loading">Loading...</div>
      ) : (
        <>
          {(error || success) && (
            <div className={`admin-faq-banner ${error ? 'error' : 'success'}`}>
              {error || success}
            </div>
          )}

          <div className="admin-faq-grid">
            <section className="admin-faq-card">
              <h3>إعدادات الصفحة</h3>
              <form onSubmit={saveSection} className="admin-faq-form">
                <div className="field">
                  <label>عنوان الصفحة</label>
                  <input
                    value={section.header_title || ''}
                    onChange={(e) => setSection((s) => ({ ...s, header_title: e.target.value }))}
                    placeholder="الأسئلة الشائعة"
                  />
                </div>
                <div className="field">
                  <label>وصف الصفحة</label>
                  <input
                    value={section.header_subtitle || ''}
                    onChange={(e) => setSection((s) => ({ ...s, header_subtitle: e.target.value }))}
                    placeholder="كل ما تحتاجين معرفته..."
                  />
                </div>
                <div className="field">
                  <label>عنوان الفوتر</label>
                  <input
                    value={section.footer_title || ''}
                    onChange={(e) => setSection((s) => ({ ...s, footer_title: e.target.value }))}
                    placeholder="لم تجدي إجابة لسؤالك؟"
                  />
                </div>
                <div className="field">
                  <label>وصف الفوتر</label>
                  <input
                    value={section.footer_subtitle || ''}
                    onChange={(e) => setSection((s) => ({ ...s, footer_subtitle: e.target.value }))}
                    placeholder="لا تترددي في التواصل..."
                  />
                </div>
                <div className="field">
                  <label>نص زر الفوتر</label>
                  <input
                    value={section.footer_button_text || ''}
                    onChange={(e) => setSection((s) => ({ ...s, footer_button_text: e.target.value }))}
                    placeholder="تواصلي معنا"
                  />
                </div>
                <div className="field">
                  <label>رابط زر الفوتر</label>
                  <input
                    value={section.footer_button_link || ''}
                    onChange={(e) => setSection((s) => ({ ...s, footer_button_link: e.target.value }))}
                    placeholder="/contact"
                    dir="ltr"
                  />
                </div>
                <button type="submit" className="btn primary" disabled={savingSection}>
                  {savingSection ? 'Saving...' : 'Save settings'}
                </button>
              </form>
            </section>

            <section className="admin-faq-card">
              <div className="admin-faq-list-header">
                <h3>الأسئلة</h3>
                <button type="button" className="btn" onClick={startCreate}>
                  + إضافة سؤال
                </button>
              </div>

              <div className="admin-faq-list">
                {faqs.length === 0 ? (
                  <div className="admin-faq-empty">لا توجد أسئلة بعد.</div>
                ) : (
                  faqs.map((f) => (
                    <div key={f.id} className="admin-faq-row">
                      <div className="admin-faq-row-main">
                        <div className="admin-faq-q">{f.question}</div>
                        <div className="admin-faq-meta">
                          <span>order: {f.sort_order}</span>
                          <span className={`status ${f.status}`}>{f.status}</span>
                        </div>
                      </div>
                      <div className="admin-faq-row-actions">
                        <button type="button" className="btn small" onClick={() => startEdit(f)}>
                          Edit
                        </button>
                        <button
                          type="button"
                          className="btn small danger"
                          onClick={() => deleteFaq(f.id)}
                          disabled={deletingId === f.id}
                        >
                          {deletingId === f.id ? 'Deleting...' : 'Delete'}
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </section>

            <section className="admin-faq-card full">
              <h3>{isEditing ? 'تحديث سؤال' : 'إضافة سؤال جديد'}</h3>
              <form onSubmit={saveFaq} className="admin-faq-form">
                <div className="field">
                  <label>السؤال</label>
                  <input
                    value={form.question}
                    onChange={(e) => setForm((s) => ({ ...s, question: e.target.value }))}
                    placeholder="اكتبي السؤال هنا"
                  />
                </div>

                <div className="field">
                  <label>الإجابة</label>
                  <textarea
                    value={form.answer}
                    onChange={(e) => setForm((s) => ({ ...s, answer: e.target.value }))}
                    rows={7}
                    placeholder="اكتبي الإجابة هنا (يمكنك استخدام أسطر جديدة)"
                  />
                </div>

                <div className="two-col">
                  <div className="field">
                    <label>الترتيب</label>
                    <input
                      type="number"
                      value={form.sort_order}
                      onChange={(e) => setForm((s) => ({ ...s, sort_order: parseInt(e.target.value || '0', 10) }))}
                    />
                  </div>
                  <div className="field">
                    <label>الحالة</label>
                    <select
                      value={form.status}
                      onChange={(e) => setForm((s) => ({ ...s, status: e.target.value }))}
                    >
                      <option value="published">published</option>
                      <option value="draft">draft</option>
                    </select>
                  </div>
                </div>

                <div className="admin-faq-form-actions">
                  <button type="submit" className="btn primary" disabled={savingFaq}>
                    {savingFaq ? 'Saving...' : isEditing ? 'Update FAQ' : 'Create FAQ'}
                  </button>
                  <button type="button" className="btn" onClick={cancelEdit} disabled={savingFaq}>
                    Clear
                  </button>
                </div>
              </form>
            </section>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminFAQEdit;

